import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Users, Shield, Waves, Droplets, RefreshCw, UtensilsCrossed, Trophy, Heart, HelpCircle, Award } from "lucide-react";

const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/assets/rrdc-logo.png`;

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginCounselor, loginAdmin, loginPool, loginDietary, loginAthletics, loginHealth, loginSupervisor, loginSpecialist } = useAuth();
  const [counselorForm, setCounselorForm] = useState({ username: "", password: "" });
  const [adminPassword, setAdminPassword] = useState("");
  const [poolPassword, setPoolPassword] = useState("");
  const [dietaryPassword, setDietaryPassword] = useState("");
  const [athleticsPassword, setAthleticsPassword] = useState("");
  const [healthPassword, setHealthPassword] = useState("");
  const [supervisorForm, setSupervisorForm] = useState({ username: "", password: "" });
  const [specialistPassword, setSpecialistPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCounselorLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginCounselor(counselorForm.username, counselorForm.password);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginAdmin(adminPassword);
      toast.success("Admin access granted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  const handlePoolLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginPool(poolPassword);
      toast.success("Pool access granted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid pool password");
    } finally {
      setLoading(false);
    }
  };

  const handleDietaryLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginDietary(dietaryPassword);
      toast.success("Dietary access granted");
      window.location.href = "/dietary";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid dietary password");
    } finally {
      setLoading(false);
    }
  };

  const handleAthleticsLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginAthletics(athleticsPassword);
      toast.success("Athletics access granted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid athletics password");
    } finally {
      setLoading(false);
    }
  };

  const handleHealthLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginHealth(healthPassword);
      toast.success("Health center access granted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid health password");
    } finally {
      setLoading(false);
    }
  };

  const handleSupervisorLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginSupervisor(supervisorForm.username, supervisorForm.password);
      toast.success("Supervisor access granted");
      navigate("/supervisor");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleSpecialistLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginSpecialist(specialistPassword);
      toast.success("Specialist access granted");
      navigate("/specialist");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid specialist password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg-pattern flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-100/50 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center mb-6 sm:mb-8">
          <img 
            src={LOGO_URL} 
            alt="Rolling River Day Camp" 
            className="w-24 h-24 sm:w-32 sm:h-32 mb-3 sm:mb-4 drop-shadow-lg"
            data-testid="login-logo"
          />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-primary-600 text-center">
            Camper Snapshot
          </h1>
        </div>

        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm">
          <a href="/help" className="flex items-center justify-center gap-2 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold rounded-t-xl transition-colors" data-testid="help-button">
            <HelpCircle className="w-4 h-4" /> Help & Tutorials
          </a>
          <Tabs defaultValue="counselor" className="w-full">
            <TabsList className="grid w-full grid-cols-4 sm:grid-cols-8 gap-1 bg-muted/50 h-auto p-1">
              <TabsTrigger 
                value="counselor" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white text-[11px] px-1"
                data-testid="counselor-tab"
              >
                <Users className="w-3.5 h-3.5 mr-0.5" />
                Counselor
              </TabsTrigger>
              <TabsTrigger 
                value="supervisor"
                className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-[11px] px-1"
                data-testid="supervisor-tab"
              >
                <Shield className="w-3.5 h-3.5 mr-0.5" />
                Supervisor
              </TabsTrigger>
              <TabsTrigger 
                value="pool"
                className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white text-[11px] px-1"
                data-testid="pool-tab"
              >
                <Droplets className="w-3.5 h-3.5 mr-0.5" />
                Pool
              </TabsTrigger>
              <TabsTrigger 
                value="dietary"
                className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-[11px] px-1"
                data-testid="dietary-tab"
              >
                <UtensilsCrossed className="w-3.5 h-3.5 mr-0.5" />
                Dietary
              </TabsTrigger>
              <TabsTrigger 
                value="athletics"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-[11px] px-1"
                data-testid="athletics-tab"
              >
                <Trophy className="w-3.5 h-3.5 mr-0.5" />
                Athletics
              </TabsTrigger>
              <TabsTrigger 
                value="health"
                className="data-[state=active]:bg-teal-600 data-[state=active]:text-white text-[11px] px-1"
                data-testid="health-tab"
              >
                <Heart className="w-3.5 h-3.5 mr-0.5" />
                Health
              </TabsTrigger>
              <TabsTrigger 
                value="specialist"
                className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-[11px] px-1"
                data-testid="specialist-tab"
              >
                <Award className="w-3.5 h-3.5 mr-0.5" />
                Specialist
              </TabsTrigger>
              <TabsTrigger 
                value="admin"
                className="data-[state=active]:bg-primary data-[state=active]:text-white text-[11px] px-1"
                data-testid="admin-tab"
              >
                <Shield className="w-3.5 h-3.5 mr-0.5" />
                Admin
              </TabsTrigger>
            </TabsList>

            <TabsContent value="counselor">
              <form onSubmit={handleCounselorLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-primary-700">
                    Group Login
                  </CardTitle>
                  <CardDescription>
                    Enter your group username and password.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="group-code">Username</Label>
                    <Input
                      id="group-code"
                      placeholder="Username"
                      value={counselorForm.username}
                      onChange={(e) => setCounselorForm(prev => ({ ...prev, username: e.target.value }))}
                      className="h-11"
                      data-testid="group-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="group-name">Group Name (Password)</Label>
                    <Input
                      id="group-name"
                      type="password"
                      placeholder="Password"
                      value={counselorForm.password}
                      onChange={(e) => setCounselorForm(prev => ({ ...prev, password: e.target.value }))}
                      className="h-11"
                      data-testid="group-name-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-primary hover:bg-primary-700 font-medium"
                    disabled={loading}
                    data-testid="counselor-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Signing in...
                      </span>
                    ) : (
                      <>
                        <Waves className="w-4 h-4 mr-2" />
                        Access My Group
                      </>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="pool">
              <form onSubmit={handlePoolLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-cyan-700">
                    Pool Access
                  </CardTitle>
                  <CardDescription>
                    Enter the pool password to view buddy sheets and swim info
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="pool-password">Pool Password</Label>
                    <Input
                      id="pool-password"
                      type="password"
                      placeholder="Enter pool password"
                      value={poolPassword}
                      onChange={(e) => setPoolPassword(e.target.value)}
                      className="h-11"
                      data-testid="pool-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 font-medium"
                    disabled={loading}
                    data-testid="pool-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Verifying...
                      </span>
                    ) : (
                      <>
                        <Droplets className="w-4 h-4 mr-2" />
                        Enter Pool Dashboard
                      </>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="dietary">
              <form onSubmit={handleDietaryLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-amber-700">
                    Dietary Access
                  </CardTitle>
                  <CardDescription>
                    Enter the dietary password to verify allergies and lunch orders
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="dietary-password">Password</Label>
                    <Input
                      id="dietary-password"
                      type="password"
                      placeholder="Enter dietary password"
                      value={dietaryPassword}
                      onChange={(e) => setDietaryPassword(e.target.value)}
                      className="h-11"
                      data-testid="dietary-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-amber-600 hover:bg-amber-700 font-medium"
                    disabled={loading}
                    data-testid="dietary-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Verifying...
                      </span>
                    ) : (
                      <>
                        <UtensilsCrossed className="w-4 h-4 mr-2" />
                        Enter Dietary Dashboard
                      </>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="athletics">
              <form onSubmit={handleAthleticsLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-blue-700">
                    Athletics Access
                  </CardTitle>
                  <CardDescription>
                    Enter the athletics password to manage programs and intramurals
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="athletics-password">Password</Label>
                    <Input
                      id="athletics-password"
                      type="password"
                      placeholder="Enter athletics password"
                      value={athleticsPassword}
                      onChange={(e) => setAthleticsPassword(e.target.value)}
                      className="h-11"
                      data-testid="athletics-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 font-medium"
                    disabled={loading}
                    data-testid="athletics-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Verifying...
                      </span>
                    ) : (
                      <>
                        <Trophy className="w-4 h-4 mr-2" />
                        Enter Athletics Dashboard
                      </>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="health">
              <form onSubmit={handleHealthLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-teal-700">
                    Health Center Access
                  </CardTitle>
                  <CardDescription>
                    Enter the health password to view camper records and add nurse notes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="health-password">Password</Label>
                    <Input
                      id="health-password"
                      type="password"
                      placeholder="Enter health password"
                      value={healthPassword}
                      onChange={(e) => setHealthPassword(e.target.value)}
                      className="h-11"
                      data-testid="health-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-teal-600 hover:bg-teal-700 font-medium"
                    disabled={loading}
                    data-testid="health-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Verifying...
                      </span>
                    ) : (
                      <>
                        <Heart className="w-4 h-4 mr-2" />
                        Enter Health Center
                      </>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="specialist">
              <form onSubmit={handleSpecialistLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-emerald-700">
                    Specialist Access
                  </CardTitle>
                  <CardDescription>
                    Shared login for camp specialists to file incident reports
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="specialist-password">Password</Label>
                    <Input
                      id="specialist-password"
                      type="password"
                      placeholder="Enter specialist password"
                      value={specialistPassword}
                      onChange={(e) => setSpecialistPassword(e.target.value)}
                      className="h-11"
                      data-testid="specialist-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-medium"
                    disabled={loading}
                    data-testid="specialist-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Verifying...
                      </span>
                    ) : (
                      <>
                        <Award className="w-4 h-4 mr-2" />
                        Enter Specialist Portal
                      </>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="supervisor">
              <form onSubmit={handleSupervisorLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-indigo-700">
                    Supervisor Login
                  </CardTitle>
                  <CardDescription>
                    Enter your division name and password
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="supervisor-division">Division</Label>
                    <Input
                      id="supervisor-division"
                      placeholder="e.g., Mariners"
                      value={supervisorForm.username}
                      onChange={(e) => setSupervisorForm(prev => ({ ...prev, username: e.target.value }))}
                      className="h-11"
                      data-testid="supervisor-division-input"
                    />
                    <p className="text-xs text-muted-foreground">Mariners, Skippers, Navigators, or Specialty</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supervisor-password">Password</Label>
                    <Input
                      id="supervisor-password"
                      type="password"
                      placeholder="Enter password"
                      value={supervisorForm.password}
                      onChange={(e) => setSupervisorForm(prev => ({ ...prev, password: e.target.value }))}
                      className="h-11"
                      data-testid="supervisor-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 font-medium"
                    disabled={loading}
                    data-testid="supervisor-login-btn"
                  >
                    {loading ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Logging in...</>
                    ) : (
                      <>Sign In</>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="admin">
              <form onSubmit={handleAdminLogin}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-heading text-primary-700">
                    Admin Access
                  </CardTitle>
                  <CardDescription>
                    Enter the admin password to manage settings and sync data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-password">Admin Password</Label>
                    <Input
                      id="admin-password"
                      type="password"
                      placeholder="Enter admin password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      className="h-11"
                      data-testid="admin-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-primary hover:bg-primary-700 font-medium"
                    disabled={loading}
                    data-testid="admin-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Verifying...
                      </span>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Enter Admin Panel
                      </>
                    )}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Rolling River Day Camp &bull; Est. 1992
        </p>
        <p className="text-center text-xs text-muted-foreground mt-3" data-testid="login-legal-links">
          <a href="/sms-optin" className="underline hover:text-foreground" data-testid="footer-sms-optin-link">SMS Opt-In</a>
          <span className="mx-2">&bull;</span>
          <a href="/sms-terms" className="underline hover:text-foreground" data-testid="footer-sms-terms-link">SMS Terms</a>
          <span className="mx-2">&bull;</span>
          <a href="/privacy-policy" className="underline hover:text-foreground" data-testid="footer-privacy-link">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
